<template>
	<div class="products-container" :class="theme">
		<div class="products">
			<div
				class="product"
				@click="$emit('select', product)"
				v-for="product in products"
				:key="product.id"
				:class="[product && active && active.id == product.id ? 'active' : '', product.theme]"
			>
				<div v-if="product.popular" class="product--popular">Most popular</div>

				<div class="product--label" v-html="product.text"></div>
				<div class="product--subtitle" v-html="product.subtitle"></div>

				<div class="product-info">
					<div
						v-if="product.badge_only_digits && product.badge_digits"
						class="product--badge product--badge-only-digits"
					>
						{{ product.badge_digits }}
					</div>
					<div class="product--badge" v-if="!product.badge_only_digits && product.badge">{{ product.badge }}</div>
					<div class="product--price-per-period" v-if="product">{{ product.price }}</div>
					<div class="product--period">{{ product.period }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { computed } from '@vue/reactivity'

const PRODUCT_COMPONENTS = ['Product', 'ProductInline', 'ProductLightened']
export default {
	props: {
		productComponent: {
			type: String,
			default: 'ProductInline',
			validator: (value) => {
				return PRODUCT_COMPONENTS.includes(value)
			}
		},
		products: {
			default: []
		},
		active: {},
		theme: {
			default: ''
		}
	},

	emits: ['select'],
	setup(props) {
		const activeProduct = computed(() => {
			const res = props.products.find((p) => p.id == props.active.id)
			return res || {}
		})
		return { activeProduct }
	}
}
</script>

<style lang="scss" scoped>
.products-container {
	margin-top: 30px;

	&.compact {
		margin-top: 20px;
	}

	@mixin summerSale($bg, $color) {
		.products {
			flex-direction: column;
		}

		.product {
			width: 100%;
			padding: 10px 15px 10px 15px;
			margin-bottom: 10px;
			border-radius: 0.7rem;
			transition: border-color 0.3s, background-color 0.3s;
			position: relative;
			background: rgba(221, 223, 240, 0.5);
			border-color: transparent;
			box-shadow: none;

			&.active {
				background: $bg;
				border-color: transparent;

				.product--label {
					color: inherit;
				}

				.product--subtitle {
					color: inherit;
				}
			}
		}

		.product--badge-only-digits {
			color: $color;
			background: none;
			font-size: 1.8rem;
			padding: 0;
			border-radius: 0;
			position: absolute;
			right: 15px;
			top: 50%;
			transform: translateY(-50%);
			font-weight: bold;

			&::before {
				content: '';
				position: absolute;
				top: 0;
				left: -10px;
				color: inherit;
				background-color: CurrentColor;
				width: 1px;
				height: 100%;
				opacity: 0.3;
			}
		}

		.product--price-per-period {
			display: none;
		}

		.product--period {
			display: none;
		}

		.product--label {
			font-weight: bold;
			font-size: 1.1rem;
			line-height: 1.3;
		}

		.product--subtitle {
			margin-top: 0.2rem;
			opacity: 0.9;
			font-size: 1rem;
			text-transform: none;
		}

		.product-info {
			padding: 0;
			margin: 0;
		}
	}

	&.ss_orange {
		@include summerSale(rgba(255, 211, 29, 0.2), #ed6d08);
	}

	&.ss_ice {
		@include summerSale(rgba(248, 125, 103, 0.1), #f87d67);
	}

	&.ss_swimsuit {
		@include summerSale(rgba(248, 138, 103, 0.1), #f87d67);
	}
}

.products {
	display: flex;
	column-gap: 0.7rem;
}

.product {
	background-color: #fff;
	padding: 1.1rem 15px 1.1rem 15px;
	margin-bottom: 10px;
	border-radius: 0.7rem;
	border: 2px solid transparent;
	transition: border-color 0.3s, background-color 0.3s, box-shadow 0.3s;
	position: relative;
	width: 50%;
	box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	&-info {
		margin-top: auto;
		padding-top: 1rem;
	}

	&.badge {
		padding-right: 6.5rem;
	}

	&--label {
		font-weight: bold;
		font-size: 1.4rem;
		line-height: 1.3;
		transition: color 0.3s;

		::v-deep i {
			font-style: normal;
			font-weight: 400;
		}

		::v-deep .inline-badge {
			background-color: #eca58f;
			color: #fff;
			font-size: 0.7rem;
			font-weight: bold;
			padding: 0.3rem 0.6rem;
			border-radius: 0.3rem;
			display: inline-flex;
			margin-left: 3px;
			letter-spacing: 0.92px;
			position: relative;
			bottom: 2px;
		}
	}

	&--subtitle {
		margin-top: 0.2rem;
		font-size: 1.16rem;
		transition: color 0.3s;

		::v-deep {
			s {
				opacity: 0.6;
			}
		}
	}

	&--badge {
		background-color: #7d8bf7;
		color: #fff;
		font-size: 0.78rem;
		font-weight: bold;
		padding: 0.2rem 0.6rem;
		border-radius: 0.3rem;
		display: inline-block;
	}

	&--price-per-period {
		margin-top: 0.5rem;
		line-height: 1.2;
		opacity: 0.5;
		transition: color 0.3s, opacity 0.3s;
	}

	&--period {
		line-height: 1.2;
		opacity: 0.5;
		transition: color 0.3s, opacity 0.3s;
	}

	&.active {
		background-color: #fff;
		border-color: #7d8bf7;
		box-shadow: none;
	}

	&.active & {
		&--label {
			color: #7d8bf7;
		}

		&--subtitle {
			color: #7d8bf7;
		}

		&--price-per-period {
			color: #7d8bf7;
			opacity: 1;
		}

		&--period {
			color: #7d8bf7;
			opacity: 1;
		}
	}

	&.orange.active {
		border-color: #ec6055;
	}

	&.orange.active & {
		&--label {
			color: #ec6055;
		}

		&--subtitle {
			color: #ec6055;
		}

		&--price-per-period {
			color: #ec6055;
			opacity: 1;
		}

		&--period {
			color: #ec6055;
			opacity: 1;
		}
	}

	&.popular {
		padding-top: 16px;
	}

	&.primary {
		&.active {
			border-color: #7d8bf7;
		}
	}

	&.primary.active & {
		&--state {
			background-color: #7d8bf7;
			border-color: #7d8bf7;
		}
	}

	&.white {
		background-color: #fff;
	}

	&.white & {
		&--state {
			display: flex;
		}
	}

	&.black {
		text-align: center;
		background-color: #2c2c2c;
		border-color: #2c2c2c;
		color: #fff;
	}

	&.black & {
		&--subtitle {
			color: #08afe6;
		}
	}

	&.centered {
		text-align: center;
		border-color: #fff;
	}

	&.centered & {
		&--subtitle {
			color: #5667ff;
		}
	}
}

.terms {
	text-align: center;
	margin-top: 1.3rem;
	font-size: 0.8rem;
	width: 90%;
	margin-left: auto;
	margin-right: auto;
}
</style>
